import React, { useCallback, useState, useEffect } from 'react';
import { ControlLink, Text, Overlay, LoadingBalls, Popover } from '@moda/om';
import { throwQueryError } from '../../../lib/apollo/throwQueryError';
import { reportError } from '../../../lib/reportError';
import { useErrorModal } from '../../../hooks/useErrorModal';
import { useAddressesQuery, RestAddress } from '../../../hooks/useAddress';
import { Box } from '../../Box';
import { PayPalButton } from '../../PaymentSourceButton';
import { PayPalShippingAddressPreview } from '../PayPalShippingAddressPreview';
import { PayPalPaymentMethodPreview } from '../PayPalPaymentMethodPreview';

import './PayPalShippingAndPayment.scss';

type Props = {
  theme?: 'box' | 'simple';
  paypalClientToken?: string | null;
  address?: RestAddress;
  addressId?: number | null;
  readOnly?: boolean;
  isSaving?: boolean;
  onSwitchToAdyen?: () => Promise<void>;
  onSubmit?: () => Promise<void>;
  onModeChange?: (mode: Mode) => void;
  showPayPalButtonTooltip?: boolean;
};

export enum Mode {
  Resting,
  Switching
}

export const PayPalShippingAndPayment: React.FC<Props> = ({
  theme = 'simple',
  paypalClientToken,
  address,
  addressId,
  readOnly,
  isSaving = false,
  onSwitchToAdyen,
  onSubmit,
  onModeChange
}) => {
  const { showErrorModal } = useErrorModal();
  const { addresses, error: addressesError } = useAddressesQuery({ skip: address != null });
  const [mode, setMode] = useState(Mode.Resting);

  const handleSwitchToAdyen = useCallback(async () => {
    setMode(Mode.Switching);
    try {
      await onSwitchToAdyen?.();
    } catch (error) {
      reportError('PAYPAL_SWITCH_TO_ADYEN', error);
      showErrorModal();
    }
    setMode(Mode.Resting);
  }, [setMode, onSwitchToAdyen, showErrorModal]);

  useEffect(() => {
    onModeChange?.(mode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const selectedAddress = address || addresses.find(address => address.id === String(addressId));

  if (addressesError) throwQueryError(addressesError);

  if (!selectedAddress) return null;

  return (
    <Box borderless={theme === 'simple'}>
      <div className="PayPalShippingAndPayment">
        <div className="PayPalShippingAndPayment__section">
          <PayPalShippingAddressPreview address={selectedAddress} />
        </div>
        <div className="PayPalShippingAndPayment__section">
          <PayPalPaymentMethodPreview />
        </div>

        {!readOnly && (
          <div className="PayPalShippingAndPayment__actions">
            <ControlLink onClick={handleSwitchToAdyen}>
              I do not want to pay with PayPal
            </ControlLink>

            <Popover
              anchor="bottomLeft"
              content={
                <div className="PayPalShippingAndPayment__tooltip">Edit Shipping Address</div>
              }
              autoPreview
              smoothTransitioning
            >
              <PayPalButton
                size="small"
                paypalClientToken={paypalClientToken ?? null}
                onSelect={onSubmit}
              />
            </Popover>
          </div>
        )}

        <Overlay
          className="PayPalShippingAndPayment__overlay"
          show={mode === Mode.Switching || isSaving}
        >
          <LoadingBalls />

          <Text treatment="eyebrow">{isSaving ? 'Saving' : 'Switching'}</Text>
        </Overlay>
      </div>
    </Box>
  );
};
